import type { GraphQLTaggedNode } from 'react-relay';
import { graphql } from 'react-relay';

export const PageTreeInitialDataNodeFragment: GraphQLTaggedNode = graphql`
	fragment PageTreeInitialDataNode_ptpage on PTPage {
		id
		title
		status
		createdDate(format: MILLIS) {
			value
		}
		hasChildren
		blank
		type
		subType
		links {
			webui
		}
		properties(
			keys: ["emoji-title-published", "emoji-title-draft", "editor", "jira-planner-connection"]
		) {
			nodes {
				id
				key
				value
				version {
					number
				}
			}
		}
	}
`;

export const PageTreeInitialDataFragment: GraphQLTaggedNode = graphql`
	fragment PageTreeInitialData_query on Query
	@argumentDefinitions(
		spaceKey: { type: "String!" }
		contentId: { type: "ID" }
		ptStatuses: { type: "[PTGraphQLPageStatus]" }
		paginationLimit: { type: "Int" }
		isSuperAdmin: { type: "Boolean!" }
		includePageTree: { type: "Boolean!" }
		includeHomepageTreeVariant: { type: "Boolean!" }
	) {
		currentPage: ptpage(
			id: $contentId
			spaceKey: $spaceKey
			pageTree: $paginationLimit
			status: $ptStatuses
			enablePaging: true
		) @include(if: $includePageTree) {
			...PageTreeInitialDataNode_ptpage @relay(mask: false)
			hasRestrictions @include(if: $isSuperAdmin)
			hasInheritedRestrictions @include(if: $isSuperAdmin)
			nearestAncestors {
				pageInfo {
					hasNextPage
					endCursor
				}
				nodes {
					...PageTreeInitialDataNode_ptpage @relay(mask: false)
					hasRestrictions @include(if: $isSuperAdmin)
					hasInheritedRestrictions @include(if: $isSuperAdmin)
					previousSiblings {
						nodes {
							...PageTreeInitialDataNode_ptpage @relay(mask: false)
							hasRestrictions @include(if: $isSuperAdmin)
							hasInheritedRestrictions @include(if: $isSuperAdmin)
						}
						pageInfo {
							hasNextPage
						}
					}
					followingSiblings {
						nodes {
							...PageTreeInitialDataNode_ptpage @relay(mask: false)
							hasRestrictions @include(if: $isSuperAdmin)
							hasInheritedRestrictions @include(if: $isSuperAdmin)
						}
						pageInfo {
							hasNextPage
						}
					}
				}
			}
			previousSiblings {
				nodes {
					...PageTreeInitialDataNode_ptpage @relay(mask: false)
					hasRestrictions @include(if: $isSuperAdmin)
					hasInheritedRestrictions @include(if: $isSuperAdmin)
				}
				pageInfo {
					hasNextPage
				}
			}
			followingSiblings {
				nodes {
					...PageTreeInitialDataNode_ptpage @relay(mask: false)
					hasRestrictions @include(if: $isSuperAdmin)
					hasInheritedRestrictions @include(if: $isSuperAdmin)
				}
				pageInfo {
					hasNextPage
				}
			}
			children {
				nodes {
					...PageTreeInitialDataNode_ptpage @relay(mask: false)
					hasRestrictions @include(if: $isSuperAdmin)
					hasInheritedRestrictions @include(if: $isSuperAdmin)
				}
				pageInfo {
					hasNextPage
				}
			}
		}
		rootPageTree: ptpage(
			spaceKey: $spaceKey
			pageTree: $paginationLimit
			status: $ptStatuses
			enablePaging: true
		) @include(if: $includePageTree) {
			...PageTreeInitialDataNode_ptpage @relay(mask: false)
			hasRestrictions @include(if: $isSuperAdmin)
			hasInheritedRestrictions @include(if: $isSuperAdmin)
			followingSiblings {
				nodes {
					...PageTreeInitialDataNode_ptpage @relay(mask: false)
					hasRestrictions @include(if: $isSuperAdmin)
					hasInheritedRestrictions @include(if: $isSuperAdmin)
				}
				pageInfo {
					hasNextPage
				}
			}
		}
		space(key: $spaceKey) {
			id
			homepage {
				id
			}
			operations {
				operation
				targetType
			}
			pageTree(pageTree: $paginationLimit, status: $ptStatuses, enablePaging: true)
				@include(if: $includeHomepageTreeVariant) {
				...PageTreeInitialDataNode_ptpage @relay(mask: false)
				hasRestrictions @include(if: $isSuperAdmin)
				hasInheritedRestrictions @include(if: $isSuperAdmin)
				nearestAncestors {
					pageInfo {
						hasNextPage
						endCursor
					}
					nodes {
						...PageTreeInitialDataNode_ptpage @relay(mask: false)
						hasRestrictions @include(if: $isSuperAdmin)
						hasInheritedRestrictions @include(if: $isSuperAdmin)
						previousSiblings {
							nodes {
								...PageTreeInitialDataNode_ptpage @relay(mask: false)
								hasRestrictions @include(if: $isSuperAdmin)
								hasInheritedRestrictions @include(if: $isSuperAdmin)
							}
							pageInfo {
								hasNextPage
							}
						}
						followingSiblings {
							nodes {
								...PageTreeInitialDataNode_ptpage @relay(mask: false)
								hasRestrictions @include(if: $isSuperAdmin)
								hasInheritedRestrictions @include(if: $isSuperAdmin)
							}
							pageInfo {
								hasNextPage
							}
						}
					}
				}
				previousSiblings {
					nodes {
						...PageTreeInitialDataNode_ptpage @relay(mask: false)
						hasRestrictions @include(if: $isSuperAdmin)
						hasInheritedRestrictions @include(if: $isSuperAdmin)
					}
					pageInfo {
						hasNextPage
					}
				}
				followingSiblings {
					nodes {
						...PageTreeInitialDataNode_ptpage @relay(mask: false)
						hasRestrictions @include(if: $isSuperAdmin)
						hasInheritedRestrictions @include(if: $isSuperAdmin)
					}
					pageInfo {
						hasNextPage
					}
				}
				children {
					nodes {
						...PageTreeInitialDataNode_ptpage @relay(mask: false)
						hasRestrictions @include(if: $isSuperAdmin)
						hasInheritedRestrictions @include(if: $isSuperAdmin)
					}
					pageInfo {
						hasNextPage
					}
				}
			}
		}
		homepageRootPageTree: ptpage(spaceKey: $spaceKey, status: $ptStatuses)
			@include(if: $includeHomepageTreeVariant) {
			...PageTreeInitialDataNode_ptpage @relay(mask: false)
			hasRestrictions @include(if: $isSuperAdmin)
			hasInheritedRestrictions @include(if: $isSuperAdmin)
			followingSiblings {
				nodes {
					...PageTreeInitialDataNode_ptpage @relay(mask: false)
					hasRestrictions @include(if: $isSuperAdmin)
					hasInheritedRestrictions @include(if: $isSuperAdmin)
				}
				pageInfo {
					hasNextPage
				}
			}
		}
	}
`;
